import * as React from 'react';
import { FC } from 'react';

import Alert from 'components/alert/alert';
import AppDownloadSection from 'components/features/components/app-download-section';
import ContentCards from 'components/features/components/braze-content-cards';
import FanFavoritesSection from 'components/features/components/fan-favorites';
import { LockedOffersSection } from 'components/features/components/locked-offers-section';
import { MainHeroContainer } from 'components/features/components/main-hero/main-hero-container';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import OurServicesSection from 'components/features/components/our-services';
import { RecentItemsSection } from 'components/features/components/recent-items';
import { RewardsSection } from 'components/features/components/rewards-section';
import Trending from 'components/features/components/trending-section';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { OffersSection } from 'components/offers-homepage-tims';
import { HomepageBanner as RollUpTheRimHomepageBanner } from 'components/roll-up-the-rim/homepage-banner';
import { IMarketingTileGroup } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import ScanForRewards from 'pages/home/scan-for-rewards';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { BRAND_ALLOWED_OFFERS_ON_HOMEPAGE } from './constants';
import { Section, Spacer } from './styles';
import { IHomePageViewProps } from './types';

export const HomePageView: FC<IHomePageViewProps> = ({ isLoading, featureHomePage }) => {
  const enableOffers = useFlag(LaunchDarklyFlag.ENABLE_OFFERS);
  const hasBottomServiceMode = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const isMobile = useMediaQuery('mobile');
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingAnimation />
      </LoadingContainer>
    );
  }
  if (featureHomePage?.components) {
    const firstItemIsMainHero = featureHomePage?.components[0]?.__typename === 'MainHero';
    return (
      <>
        {firstItemIsMainHero ? null : <Spacer $hasBottomServiceMode={hasBottomServiceMode} />}
        {featureHomePage.components.map((item, idx) => {
          if (!item) {
            return null;
          }
          const first = idx === 0;
          const last = idx + 1 === (featureHomePage.components?.length || 0);
          switch (item.__typename) {
            case 'MainHero': {
              return (
                <div key={`HomePage_MainHero_${item._id}`}>
                  <MainHeroContainer item={item} />
                </div>
              );
            }

            case 'BrazeContentCards': {
              return (
                <div key={`HomePage_BrazeContentCards_${item._id}`}>
                  <ContentCards item={item} />
                </div>
              );
            }

            case 'Alert': {
              return (
                <div key={`HomePage_Alert_${item._id}`}>
                  <Alert
                    headline={item.heading?.locale || ''}
                    body={item.body?.locale || ''}
                    learnMore={item.learnMoreText?.locale || ''}
                    learnMoreUrl={item.learnMoreUrl?.locale || ''}
                  />
                </div>
              );
            }

            case 'MarketingTileGroup':
              return (
                <Section key={`HomePage_MarketingTileGroup_${item._id}`} first={first} last={last}>
                  <MarketingTileGroup item={item as IMarketingTileGroup} />
                </Section>
              );

            case 'OffersSection': {
              return (
                enableOffers &&
                BRAND_ALLOWED_OFFERS_ON_HOMEPAGE && (
                  <div key={`HomePage_OffersSection_${item._id}`}>
                    <OffersSection />
                  </div>
                )
              );
            }

            case 'Trending': {
              return (
                <div key={`HomePage_Trending_${item._id}`}>
                  <Trending item={item} />
                </div>
              );
            }

            case 'FanFavorites': {
              return (
                <div key={`HomePage_FanFavorites_${item._id}`}>
                  <FanFavoritesSection item={item} />
                </div>
              );
            }

            case 'OurServices': {
              return (
                <div key={`HomePage_OurServices_${item._id}`}>
                  <OurServicesSection item={item} />
                </div>
              );
            }

            case 'AppDownload': {
              return (
                <div key={`HomePage_AppDownload_${item._id}`}>
                  <AppDownloadSection item={item?.details} />
                </div>
              );
            }

            case 'ScanForRewards': {
              return (
                isMobile && (
                  <div key={`HomePage_ScanForRewards_${item._id}`}>
                    <ScanForRewards />
                  </div>
                )
              );
            }

            case 'RollUpTheRimSection': {
              return (
                <div key={`HomePage_RollUpTheRimSection_${item._id}`}>
                  <RollUpTheRimHomepageBanner />
                </div>
              );
            }

            case 'RecentItems': {
              return (
                <div key={`HomePage_RecentItems_${item._id}`}>
                  <RecentItemsSection />
                </div>
              );
            }

            case 'LockedOffers': {
              return (
                <div key={`HomePage_LockedOffers_${item._id}`}>
                  <LockedOffersSection />
                </div>
              );
            }

            case 'RewardsSection': {
              return (
                <div key={`HomePage_RewardsSection_${item._id}`}>
                  <RewardsSection item={item} />
                </div>
              );
            }

            default:
              return null;
          }
        })}
      </>
    );
  }
  return null;
};
